import axios from "axios";
import { toast } from "react-toastify";

import { getUserCredentials } from "../utils/common";
import { API_URL } from "../utils/Constant";
import {
  getViewAsPerStatusCode, formatDateForDisplay, isNonEmptyField, downloadFileFromS3WithPresigned,
} from "../utils/helpers";

/**
 * get all the fields for product reports
 * @returns {Object} {success, message, data[0].Fields[0]}
 */
export const getProductFields = async (skipMaintenanceRedirect = false, extraParams = {}) => {
  try {
    const resp = await axios.get(`${API_URL}/productfields/`, {
      params: extraParams.side ? { side: extraParams.side } : undefined,
    });
    const {
      fields,
      link_report,
      sampleid_fields,
    } = resp?.data?.result;

    if (fields && fields.length > 0) {
      const fieldsMap = {};
      fields.forEach((fieldObj) => {
        fieldsMap[fieldObj.json_field] = fieldObj;
      });
      return {
        success: true, fields, fieldsMap, link_report, sampleid_fields,
      };
    }
    const message = "No data from API.";
    return { success: false, message };
  } catch (e) {
    if (!skipMaintenanceRedirect) {
      getViewAsPerStatusCode(e);
    }
    const message = "Something went wrong.";
    return { success: false, message };
  }
};

/**
 * gets all the product reports for the reqUrl or the first page, that are a result of the applied search filters
 * @param {string} reqUrl pagelink for which all the product reports are to be fetched
 * @param {*} params {fromm, to, specs_flag, search, retest, review_results_alert}
 * @returns {Object} {files, prevPageLink, nextPageLink, lastPageLink, totalPage, dates}
 */

export const getReports = async (reqUrl, params, signal = null) => {
  try {
    let response;
    if (!reqUrl) {
      response = await axios.get(`${API_URL}/reports/`, {
        params: {
          page: "1",
          fromm: "",
          to: "",
          specs_flag: "",
          search: "",
          retest: "",
          review_results_alert: "",
          ...params,
        },
        signal,
      });
    } else {
      response = await axios.get(`${API_URL}${reqUrl.substr(4)}`, { signal }); // take substring to remove '/api' at the beginning of the url (it's already incuded in API_URL)
    }
    const data = await response.data;
    const {
      prev_page_link: prevPageLink, next_page_link: nextPageLink, last_page_link: lastPageLink, total_page: totalPage, dates,
    } = data;
    const res = {
      files: [], prevPageLink, nextPageLink, lastPageLink, totalPage, dates,
    };
    if (isNonEmptyField(data, "result") && isNonEmptyField(data.result, 0)) {
      data.result.forEach((report) => {
        if (report.received_date) {
          report.received_date = formatDateForDisplay(report.received_date);
        }
        if (report.report_date) {
          report.report_date = formatDateForDisplay(report.report_date);
        }
      });
      res.files = data.result;
    }
    return { success: true, ...res };
  } catch (e) {
    // getViewAsPerStatusCode(e);
    return { success: false, message: e.response?.data?.message || "Something went wrong.", canceled: axios.isCancel(e) };
  }
};

/**
 * used to rearrange or change the positions of the dynamic table fields
 * @param {*} payload {field, position} the name of the table column to be re-arranged and the new position of the table column.
 * @returns {Object} {success, res} object returning table fields in the new order
 */
export const changePosition = async (payload) => {
  try {
    const params = {
      field: payload.field || "",
      position: payload.position || "",
    };
    const response = await axios.get(`${API_URL}/productfieldindexing/`, { params });
    const data = await response.data;

    return { success: true, fields: data.result };
  } catch (e) {
    // getViewAsPerStatusCode(e);
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Used to change the field names of the product report table
 * @param {} payload {field, new_field} takes the old and new names of the table field
 * @returns {Object} {success, res} returns the edited values of the product table fields
 */
export const editTableTitle = async (payload) => {
  try {
    const params = {
      field: payload.field,
      new_field: payload.new_field,
    };
    const response = await axios.get(`${API_URL}/editproductfields/`, { params });
    const data = await response.data;

    return { success: true, fields: data.result };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * gets the spec card (triggered when clicked on the the status of the results in specification column)
 * @param {*} payload {index, index_id, report_id, sample_id, received_date, sample_type, method_reference}
 * @returns {Object} {success, res={index0, specs_flag, title, report_result, card_id}}
 */
export const getSpec = async (payload, methodReference = false) => {
  try {
    const params = {
      index: payload.index,
      index_id: payload.index_id,
      report_id: payload.report_id,
      sample_id: payload.sample_id,
      received_date: payload.received_date,
      sample_type: payload.sample_type,
      method_reference: methodReference,
      ...(payload.side ? { side: payload.side } : {}),
    };

    const response = await axios.get(`${API_URL}/reportspecs/`, { params });
    const data = await response.data;
    const res = { spec: {}, pending_retest_submission_data: null };
    if (data && data.length > 0) {
      const envelope = data[0];
      res.pending_retest_submission_data = envelope.pending_retest_submission_data || null;
      const result = envelope.result;
      if (result && result.length > 0) {
        const spec = result[0];
        res.spec = spec;
      }
    }

    return { success: true, ...res };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * used to send the retest request
 * @param {*} payload {sample_id, received_at, tests, sample_type}
 * @returns {} message
 */
export const requestRetest = async (payload) => {
  try {
    const user = getUserCredentials();
    const params = {
      user_email: user.user_email || "",
      sample_id: payload.sample_id,
      received_at: payload.received_at,
      tests: payload.tests,
      sample_type: payload.sample_type,
    };
    const response = await axios.post(`${API_URL}/requestretest/`, params);
    if (response && response.data && response.data.message === "Success") {
      return { success: true };
    }
    return { success: false, message: response.data.message || "Retest request has failed." };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Retest request has failed." };
  }
};

/**
 * Release held Out of Spec / Out of Trend test results to COA.
 * POST /api/releaseresults/
 * @param {Object} payload
 * @param {string} payload.sample_id
 * @param {string} payload.sample_type
 * @param {string} payload.received_date YYYY-MM-DD
 * @param {Array<{test: string, request_date: string}>} payload.test_list
 * @param {boolean} [payload.is_under_review=false]
 * @returns {Object} {success, message?}
 */
export const releaseTestResults = async (payload) => {
  try {
    const user = getUserCredentials();
    const params = {
      user_payload: user?.user_payload || "",
      company_name: user?.company_name || "",
      sample_id: payload.sample_id || "",
      sample_type: payload.sample_type || "",
      received_date: payload.received_date || "",
      is_under_review: payload.is_under_review === true,
      test_list: Array.isArray(payload.test_list) ? payload.test_list : [],
      user_email: user?.user_email || "",
      support: user?.uid === "support",
    };
    const response = await axios.post(`${API_URL}/releaseresults/`, params);
    if (response && response.data && response.data.message === "Success") {
      return { success: true };
    }
    return { success: false, message: response?.data?.message || "Failed to release test results." };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Failed to release test results." };
  }
};

/**
 * Submit a Contact CSM request for selected review tests.
 * POST /api/contactcsm/
 * @param {Object} payload
 * @param {string} payload.sample_id
 * @param {string} payload.sample_type
 * @param {string} payload.received_date YYYY-MM-DD
 * @param {string} payload.release_result_status
 * @param {Array<{test: string, request_date: string}>} payload.test_list
 * @param {string} [payload.comment]
 * @param {string} [payload.contact_csm_requested_by]
 * @param {string} [payload.sample_view_url]
 * @returns {Object} {success, message?}
 */
export const contactCsm = async (payload) => {
  try {
    const user = getUserCredentials();
    const params = {
      user_payload: user?.user_payload || "",
      company_name: user?.company_name || "",
      sample_id: payload.sample_id || "",
      sample_type: payload.sample_type || "",
      received_date: payload.received_date || "",
      release_result_status: payload.release_result_status || "",
      test_list: Array.isArray(payload.test_list) ? payload.test_list : [],
      comment: payload.comment || "",
      contact_csm_requested_by: payload.contact_csm_requested_by
        || user?.user_name
        || "",
      user_email: user?.user_email || "",
      support: user?.uid === "support",
      sample_view_url: payload.sample_view_url || "",
    };
    const response = await axios.post(`${API_URL}/contactcsm/`, params);
    if (response && response.data && response.data.message === "Success") {
      return { success: true };
    }
    return { success: false, message: response?.data?.message || "Failed to submit CSM request." };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Failed to submit CSM request." };
  }
};

/**
 * Notify QC that the user chose not to update specs for released tests.
 * POST /api/contactcsm/
 * @param {Object} payload
 * @param {string} payload.sample_id
 * @param {string} payload.sample_type
 * @param {string} payload.received_date YYYY-MM-DD
 * @param {Array<{test: string, request_date: string}>} payload.test_list
 * @returns {Object} {success, message?}
 */
export const notifyDontUpdateSpecs = async (payload) => {
  try {
    const user = getUserCredentials();
    const params = {
      flow: "qc_notify_dont_update_specs",
      user_payload: user?.user_payload || "",
      company_name: user?.company_name || "",
      sample_id: payload.sample_id || "",
      sample_type: payload.sample_type || "",
      received_date: payload.received_date || "",
      test_list: Array.isArray(payload.test_list) ? payload.test_list : [],
    };
    const response = await axios.post(`${API_URL}/contactcsm/`, params);
    if (response && response.data && response.data.message === "Success") {
      return { success: true };
    }
    return { success: false, message: response?.data?.message || "Failed to notify QC." };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Failed to notify QC." };
  }
};

/**
 * Request a retest using a new sample.
 * @param {Object} payload
 * @param {string} payload.parent_submission_id
 * @param {string} [payload.parent_sample_unique_identifier]
 * @param {string} [payload.parent_sample_id]
 * @param {string} [payload.parent_sample_type]
 * @param {string} [payload.parent_received_date]
 * @param {string[]} payload.tests_selected
 * @param {string} payload.scheduled_date
 * @param {string} [payload.send_new_sample]
 * @returns {Object} {success, message, submissionId?, scheduledDate?}
 */
export const requestRetestNewSample = async (payload) => {
  try {
    const user = getUserCredentials();
    const parentSampleUniqueIdentifier = payload.parent_sample_unique_identifier || "";
    const params = {
      user_email: user.user_email || "",
      parent_submission_id: payload.parent_submission_id || "",
      parent_sample_unique_identifier: parentSampleUniqueIdentifier,
      tests_selected: payload.tests_selected || [],
      scheduled_date: payload.scheduled_date || "",
      submitted_by: user.user_name || "",
    };

    if (payload.send_new_sample) {
      params.send_new_sample = payload.send_new_sample;
    }

    if (!parentSampleUniqueIdentifier) {
      params.parent_sample_id = payload.parent_sample_id || "";
      params.parent_sample_type = payload.parent_sample_type || "";
      params.parent_received_date = payload.parent_received_date || "";
    }

    const response = await axios.post(`${API_URL}/create_new_retest_sample/`, params);
    if (response && response.data && response.data.message === "Success") {
      return {
        success: true,
        submissionId: response.data.submission_id || response.data.result?.submission_id || "",
        scheduledDate: response.data.scheduled_date || params.scheduled_date || "",
      };
    }
    return { success: false, message: response.data.message || "New sample retest request has failed." };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "New sample retest request has failed." };
  }
};

/**
 * Used to share product report via email
 * @param {*} payload {recipients_email_address_lst, email_user_input, sample_id, received_date, reports, index_id}
 * @returns
 * // page not found error
 */
export const sendShareEmails = async (payload) => {
  try {
    const user = getUserCredentials();
    const response = await axios.post(`${API_URL}/sharetestresults/`, {
      recipients_email_address_lst: payload.email_list,
      email_user_input: payload.message,
      user_name: user.user_name || "dev",
      sample_id: payload.sample_id,
      received_date: payload.received_date,
      reports: payload.reports,
      index_id: payload.index_id,
    });

    return { success: true, ...response.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Fetches pesticide report detail.
 * @param {object} params Object of sample_id, sample_type, received_date and retest_date
 * @returns {object} pesticide detailed data
 */
export const getReportSpecDetail = async ({
  sample_id, sample_type, received_date, retest_date, test,
}) => {
  try {
    const params = {
      sample_id,
      sample_type,
      received_date,
      retest_date,
      test,
    };
    const response = await axios.get(`${API_URL}/reportspecsdetail/`, { params });
    return { success: true, ...response.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

export const getPanelReportSpecDetail = async ({
  sample_id, sample_type, received_date, retest_date, test,
}) => {
  try {
    const user = getUserCredentials();
    const params = {
      user_payload: user?.user_payload || "",
      company_name: user?.company_name || "",
      sample_id,
      sample_type,
      received_date,
      retest_date,
      test,
    };
    const response = await axios.get(`${API_URL}/panelreportspecdetails/`, { params });
    return { success: true, ...response.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Calls api to update countries of specified company
 * @param {string} customer_id Customer id
 * @param {string[]} countries_lst List of country codes
 */
export const updatePesticideCountrySpecDetails = async (countries_lst) => {
  try {
    const payload = { countries_lst };
    const response = await axios.patch(`${API_URL}/reportspecsdetailupdatecountries/`, payload);
    return { success: true, ...response.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Get report history for a product sample
 * @param {Object} payload { sample_id, sample_type, received_date, action }
 * @returns {Object} { success, message, reports: [{ s3_report_path, report_type, report_category, received_date }] }
 */
export const getReportHistory = async (payload = {}) => {
  try {
    // Get user credentials for user_payload and company_name
    const user = getUserCredentials();

    const response = await axios.get(`${API_URL}/reporthistory/`, {
      params: {
        user_payload: user?.user_payload || "",
        company_name: user?.company_name || "",
        sample_id: payload.sample_id || "",
        sample_type: payload.sample_type || "Product",
        received_date: payload.received_date || "",
        action: payload.action || "partial", // "partial" or "all_report"
      },
    });

    const { message, result } = response.data;
    return {
      success: message === "Success",
      message,
      reports: result || [],
    };
  } catch (e) {
    const message = e.response?.data?.message || "Something went wrong.";
    return { success: false, message, reports: [] };
  }
};

/**
 * Handle downloading multiple partial report files
 * @param {Array} fileArray Array of file objects with s3_report_path
 */
const handleDownloadPartialReports = async (fileArray) => {
  if (!fileArray || fileArray?.length === 0) {
    toast.error("Failed to download Partial Report");
    return;
  }

  // Collect all file paths from the result array
  // API response structure: { path: ["path1", "path2"], error: [], ... }
  const allFilePaths = [];
  const errors = [];

  fileArray.forEach((file) => {
    // Check for errors in the response
    if (file.error && Array.isArray(file.error) && file.error.length > 0) {
      errors.push(...file.error);
    }

    // Handle path array from API response (new structure)
    if (file.path && Array.isArray(file.path) && file.path.length > 0) {
      file.path.forEach((filePath) => {
        if (filePath) {
          allFilePaths.push(filePath);
        }
      });
    } else if (file.s3_report_path) {
      // Fallback: handle legacy s3_report_path structure (for backward compatibility)
      allFilePaths.push(file.s3_report_path);
    }
  });

  // Check if there are errors or no file paths - show single error message
  if (errors.length > 0 || allFilePaths.length === 0) {
    toast.error("Failed to download Partial Report");
    return;
  }

  // Download all files
  const downloadFilePromises = allFilePaths.map((filePath) => {
    const fileName = filePath.split("/").pop() || "";
    const isPrivateS3Bucket = filePath.includes("COAs/");
    if (isPrivateS3Bucket) {
      return downloadFileFromS3WithPresigned([filePath], "private", [fileName]);
    }
    return downloadFileFromS3WithPresigned([filePath], "public", [fileName]);
  });

  const resultsArray = await Promise.allSettled(downloadFilePromises);
  const failedCount = resultsArray.filter((result) => result.status === "rejected").length;
  const successCount = resultsArray.filter((result) => result.status === "fulfilled").length;

  if (successCount === 0) {
    toast.error("Failed to download Partial Report");
  } else if (failedCount === 0) {
    toast.success(`Partial report generated and downloaded successfully ${successCount}/${resultsArray.length}`);
  } else {
    toast.warning(`Downloaded ${successCount}/${resultsArray.length} files. Some files could not be downloaded.`);
  }
};

/**
 * Generate partial report for selected tests (or regenerate edited COA via report_sample_id / report_sample_type).
 * @param {Object} payload
 * @param {Array} payload.request_list - [{ sample_id, sample_type, received_date, date, test_list?, report_sample_id?, report_sample_type? }]
 * @param {boolean} [payload.skipAutoDownload=false] - When true, skip auto-download (used by Regenerate COA)
 * @returns {Object} { success, message, result: [{ s3_report_path, report_type }] }
 */
export const generatePartialReport = async (payload = {}) => {
  try {
    const response = await axios.post(`${API_URL}/partialreport/`, {
      request_list: payload.request_list || [],
    });

    const { message, result } = response.data;
    const shouldAutoDownload = !payload.skipAutoDownload;

    // Automatically download all generated reports (Generate Partial only; Regenerate skips this)
    if (
      shouldAutoDownload
      && message === "Success"
      && result
      && Array.isArray(result)
      && result.length > 0
    ) {
      try {
        await handleDownloadPartialReports(result);
      } catch (downloadError) {
        // Don't fail the entire operation if download fails
        // eslint-disable-next-line no-console
        console.error("Failed to auto-download reports:", downloadError);
      }
    }

    return {
      success: message === "Success",
      message,
      result: result || [],
    };
  } catch (e) {
    const message = e.response?.data?.message || "Something went wrong.";
    return { success: false, message, result: [] };
  }
};

/**
 * Save sample ID edit history (log-only; does not mutate live sample)
 * @param {Object} payload { sample_id, sample_type, received_date, new_sample_id, new_sample_type, customized_fields }
 * @returns {Object} { success, message }
 */
export const saveSampleIdEditHistory = async (payload = {}) => {
  try {
    const user = getUserCredentials();

    const response = await axios.post(`${API_URL}/sampleidedithistory/`, {
      sample_id: payload.sample_id,
      sample_type: payload.sample_type,
      received_date: payload.received_date,
      new_sample_id: payload.new_sample_id,
      new_sample_type: payload.new_sample_type,
      customized_fields: payload.customized_fields,
      user_email: user?.user_email,
    });

    const { message } = response.data;
    return {
      success: message === "Success",
      message,
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message };
  }
};

/**
 * Save sample type edit history (log-only; does not mutate live sample)
 * @param {Object} payload { sample_id, sample_type, received_date, new_sample_type }
 * @returns {Object} { success, message }
 */
export const saveSampleTypeEditHistory = async (payload = {}) => {
  try {
    const user = getUserCredentials();

    const response = await axios.post(`${API_URL}/sampletypeedithistory/`, {
      sample_id: payload.sample_id,
      sample_type: payload.sample_type,
      received_date: payload.received_date,
      new_sample_type: payload.new_sample_type,
      user_email: user?.user_email,
    });

    const { message } = response.data;
    return {
      success: message === "Success",
      message,
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message };
  }
};

/**
 * Get sample ID edit history for change history modal
 * @param {Object} payload { sample_id, sample_type, received_date }
 * @returns {Object} { success, message, history }
 */
export const getSampleIdEditHistory = async (payload = {}) => {
  try {
    const response = await axios.get(`${API_URL}/sampleidedithistory/`, {
      params: {
        sample_id: payload.sample_id,
        sample_type: payload.sample_type,
        received_date: payload.received_date,
      },
    });

    const { message, result } = response.data;
    return {
      success: message === "Success",
      message,
      history: result || [],
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message, history: [] };
  }
};

/**
 * Get sample type edit history for change history modal
 * @param {Object} payload { sample_id, sample_type, received_date }
 * @returns {Object} { success, message, history }
 */
export const getSampleTypeEditHistory = async (payload = {}) => {
  try {
    const response = await axios.get(`${API_URL}/sampletypeedithistory/`, {
      params: {
        sample_id: payload.sample_id,
        sample_type: payload.sample_type,
        received_date: payload.received_date,
      },
    });

    const { message, result } = response.data;
    return {
      success: message === "Success",
      message,
      history: result || [],
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message, history: [] };
  }
};

/**
 * Preview unit conversion (Convert button only).
 * @param {Object} payload { test_name, result, conversion_unit }
 * @returns {Object} { success, message, result, unit, status }
 */
export const convertUnit = async (payload = {}) => {
  try {
    const user = getUserCredentials();
    const response = await axios.post(`${API_URL}/unitconversion/`, {
      user_email: user?.user_email || "",
      action: "Unit conversion",
      test_name: payload.test_name || "",
      result: payload.result ?? "",
      conversion_unit: payload.conversion_unit || "",
    });

    const data = response.data || {};
    return {
      ...data,
      success: data.status === "Success",
    };
  } catch (e) {
    const data = e.response?.data || {};
    return {
      success: false,
      ...data,
      status: data.status || "Error",
      message: data.message || "Something went wrong.",
    };
  }
};

/**
 * Save unit conversion result for a test.
 * @param {Object} payload
 * @returns {Object} { success, message }
 */
export const saveUnitConversionResult = async (payload = {}) => {
  try {
    const user = getUserCredentials();
    const response = await axios.post(`${API_URL}/unitconversion/`, {
      user_email: user?.user_email || "",
      action: "Save updated result and spec",
      sample_id: payload.sample_id || "",
      sample_type: payload.sample_type || "",
      received_date: payload.received_date || "",
      test: payload.test || "",
      date: payload.date || "",
      retest_sample_usid: payload.retest_sample_usid || "",
      converted_result: payload.converted_result ?? "",
      converted_spec: payload.converted_spec ?? "",
      converted_unit: payload.converted_unit || "",
    });

    const { message } = response.data || {};
    return {
      success: message === "Success",
      message,
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message };
  }
};

/**
 * Reset all units to default for a sample/date.
 * @param {Object} payload
 * @returns {Object} { success, message }
 */
export const setUnitsToDefault = async (payload = {}) => {
  try {
    const user = getUserCredentials();
    const response = await axios.post(`${API_URL}/unitconversion/`, {
      user_email: user?.user_email || "",
      action: "Set units to default",
      sample_id: payload.sample_id || "",
      sample_type: payload.sample_type || "",
      received_date: payload.received_date || "",
      date: payload.date || "",
    });

    const { message } = response.data || {};
    return {
      success: message === "Success",
      message,
    };
  } catch (error) {
    const message = error.response?.data?.message || "Something went wrong.";
    return { success: false, message };
  }
};
